import type { SalePublic } from "@/lib/offline/db";
import type { TicketContext } from "@/lib/printing";
import { CMD, concatBytes, encodeCp850 } from "./commands";

export interface EscPosOptions {
  openDrawer?: boolean;
  cutPaper?: boolean;
  beep?: boolean;
}

function moneyFormat(cents: number, currency = "MXN"): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function padLine(left: string, right: string, width: number): string {
  const maxSpace = width - left.length - right.length;
  const space = Math.max(1, maxSpace);
  return left + " ".repeat(space) + right;
}

/**
 * Genera el flujo binario completo de comandos ESC/POS para una venta.
 */
export function buildEscPosPayload(
  sale: SalePublic,
  context: TicketContext,
  options: EscPosOptions = {},
): Uint8Array {
  const width = context.paperWidth === 58 ? 32 : 48;
  const rule = "-".repeat(width);
  const parts: Uint8Array[] = [];

  const addText = (text: string) => {
    parts.push(encodeCp850(text));
    parts.push(CMD.LF);
  };

  // 1. Inicialización y Selección de Página de Códigos CP850
  parts.push(CMD.INIT);
  parts.push(CMD.CODEPAGE_CP850);

  if (options.beep) {
    parts.push(CMD.BEEP);
  }

  // 2. Apertura de cajón de dinero (si aplica)
  if (options.openDrawer) {
    parts.push(CMD.DRAWER_PIN2);
    parts.push(CMD.DRAWER_PIN5);
  }

  // 3. Encabezado del Negocio (Centrado)
  parts.push(CMD.ALIGN_CENTER);
  parts.push(CMD.BOLD_ON);
  parts.push(CMD.SIZE_DOUBLE);
  addText(context.businessName.toUpperCase().slice(0, Math.floor(width / 2)));
  parts.push(CMD.SIZE_NORMAL);
  parts.push(CMD.BOLD_OFF);

  if (context.slogan) {
    addText(context.slogan.slice(0, width));
  }
  if (context.rfc) {
    addText(`RFC: ${context.rfc}`.slice(0, width));
  }
  if (context.taxRegime) {
    addText(`Regimen: ${context.taxRegime}`.slice(0, width));
  }
  if (context.address) {
    addText(context.address.slice(0, width));
  }
  if (context.phone) {
    addText(`Tel: ${context.phone}`.slice(0, width));
  }
  if (context.showBranch !== false && context.branchName) {
    parts.push(CMD.BOLD_ON);
    addText(`Sucursal: ${context.branchName}`.slice(0, width));
    parts.push(CMD.BOLD_OFF);
  }

  if (context.headerNote) {
    addText(rule);
    addText(context.headerNote.slice(0, width));
  }

  // 4. Datos del Ticket
  parts.push(CMD.ALIGN_LEFT);
  addText(rule);
  if (context.showFolio !== false) {
    parts.push(CMD.BOLD_ON);
    addText(padLine(`Ticket #${sale.series}-${sale.folio}`, sale.business_date, width));
    parts.push(CMD.BOLD_OFF);
  }
  if (context.showCashier !== false) {
    addText(padLine("Cajero", context.cashierName, width));
  }
  addText(rule);

  // 5. Partidas / Productos
  for (const item of sale.lines ?? []) {
    parts.push(CMD.BOLD_ON);
    addText(item.name.slice(0, width));
    parts.push(CMD.BOLD_OFF);

    const detail = `  ${item.quantity} x ${moneyFormat(item.unit_price_cents, context.currency)}`;
    const lineTotal = moneyFormat(item.total_cents, context.currency);
    addText(padLine(detail, lineTotal, width));

    if (item.discount_cents > 0) {
      const discount = `-${moneyFormat(item.discount_cents, context.currency)}`;
      addText(padLine("  Descuento", discount, width));
    }
  }

  // 6. Subtotal, Impuestos y TOTAL
  addText(rule);
  addText(padLine("Subtotal", moneyFormat(sale.subtotal_cents, context.currency), width));

  if (sale.discount_cents > 0) {
    addText(padLine("Descuento", `-${moneyFormat(sale.discount_cents, context.currency)}`, width));
  }

  if (context.showTaxBreakdown !== false) {
    addText(padLine("IVA", moneyFormat(sale.tax_cents, context.currency), width));
  }

  // Total destacado
  parts.push(CMD.BOLD_ON);
  parts.push(CMD.SIZE_DOUBLE_HEIGHT);
  addText(padLine("TOTAL", moneyFormat(sale.total_cents, context.currency), width));
  parts.push(CMD.SIZE_NORMAL);
  parts.push(CMD.BOLD_OFF);
  addText(rule);

  // 7. Desglose de Pago
  for (const payment of sale.payments ?? []) {
    addText(padLine(payment.method.toUpperCase(), moneyFormat(payment.amount_cents, context.currency), width));
  }
  if (sale.change_cents > 0) {
    parts.push(CMD.BOLD_ON);
    addText(padLine("CAMBIO", moneyFormat(sale.change_cents, context.currency), width));
    parts.push(CMD.BOLD_OFF);
  }
  if (sale.tip_cents > 0) {
    addText(padLine("Propina", moneyFormat(sale.tip_cents, context.currency), width));
  }

  // 8. Pie de Ticket
  parts.push(CMD.ALIGN_CENTER);
  addText(rule);
  if (context.footerNote) {
    addText(context.footerNote);
  } else {
    addText("¡Gracias por su compra!");
  }

  if (context.billingInstructions) {
    parts.push(CMD.LF);
    addText(context.billingInstructions);
  }

  // 9. Espaciado y Corte de papel
  parts.push(CMD.LF);
  parts.push(CMD.LF);

  if (options.cutPaper !== false) {
    parts.push(CMD.CUT_FEED_3);
  }

  return concatBytes(...parts);
}

/**
 * Genera un ticket de prueba para calibración de hardware e impresora térmica.
 */
export function buildTestTicketPayload(
  businessName: string,
  paperWidth: 58 | 80,
  openDrawer = true,
): Uint8Array {
  const width = paperWidth === 58 ? 32 : 48;
  const rule = "=".repeat(width);
  const parts: Uint8Array[] = [];

  const addText = (text: string) => {
    parts.push(encodeCp850(text));
    parts.push(CMD.LF);
  };

  parts.push(CMD.INIT);
  parts.push(CMD.CODEPAGE_CP850);

  if (openDrawer) {
    parts.push(CMD.DRAWER_PIN2);
    parts.push(CMD.DRAWER_PIN5);
  }

  parts.push(CMD.ALIGN_CENTER);
  parts.push(CMD.BOLD_ON);
  parts.push(CMD.SIZE_DOUBLE);
  addText(businessName.toUpperCase().slice(0, Math.floor(width / 2)));
  parts.push(CMD.SIZE_NORMAL);
  parts.push(CMD.BOLD_OFF);

  addText("SISTEMA MEGASYR POS");
  addText("PRUEBA DE HARDWARE ESC/POS");
  addText(rule);

  parts.push(CMD.ALIGN_LEFT);
  addText(`Papel: ${paperWidth} mm (${width} columnas)`);
  addText(`Fecha: ${new Date().toLocaleString("es-MX")}`);
  addText("Acentos test: á é í ó ú ñ Ñ ¡ ¿ $");
  addText(padLine("Columna Izquierda", "Columna Der.", width));
  addText(padLine("Cajón de dinero:", openDrawer ? "Disparado OK" : "Sin pulso", width));
  addText(rule);

  parts.push(CMD.ALIGN_CENTER);
  parts.push(CMD.BOLD_ON);
  addText("¡IMPRESIÓN DIRECTA EXITOSA!");
  parts.push(CMD.BOLD_OFF);

  parts.push(CMD.LF);
  parts.push(CMD.LF);
  parts.push(CMD.CUT_FEED_3);

  return concatBytes(...parts);
}
